import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Check, CheckCircle2, CreditCard, HeartHandshake, Loader2, Lock, Star, XCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CommunityPartnerInquiryModal } from "@/components/CommunityPartnerInquiryModal";
import EnterpriseInquiryModal from "@/components/EnterpriseInquiryModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [
  {
    name: "listed",
    displayName: "Listed",
    tierNumber: "Tier 0",
    price: "Free",
    priceNote: "Always free",
    tagline: "Get discovered.",
    features: [
      "Business name + phone in your service area",
      "Listed under your correct care type(s)",
      "Zero cost, forever",
    ],
    cta: "Start Free"
  },
  {
    name: "essential",
    displayName: "Essential",
    tierNumber: "Tier 1",
    price: "$459/year",
    originalPrice: "$499/year",
    promoLabel: "🎉 Launch offer — 50% off your first 2 months (limited time)",
    tagline: "Tell your story.",
    features: [
      "Everything in Listed",
      "Full business description",
      "Company logo",
      "Website + email links",
      "Business hours",
    ],
    cta: "Choose Essential"
  },
  {
    name: "enhanced",
    displayName: "Enhanced",
    tierNumber: "Tier 2",
    price: "$729/year",
    originalPrice: "$799/year",
    promoLabel: "🎉 Launch offer — 50% off your first 2 months (limited time)",
    tagline: "Show your services.",
    features: [
      "Everything in Essential",
      "Photo gallery",
      "Full services list",
      "Insurance accepted list",
      "Languages spoken",
    ],
    cta: "Choose Enhanced"
  },
  {
    name: "premium",
    displayName: "Premium",
    tierNumber: "Tier 3",
    price: "$1,829/year",
    originalPrice: "$1,999/year",
    promoLabel: "🎉 Launch offer — 50% off your first 2 months (limited time)",
    tagline: "Meet families face-to-face.",
    features: [
      "Everything in Enhanced",
      "Intro video on your profile",
      "In-app Virtual Consult booking",
      "Detailed analytics dashboard",
    ],
    cta: "Choose Premium"
  },
  {
    name: "enterprise",
    displayName: "Enterprise",
    tierNumber: "Tier 4",
    price: "Contact us",
    priceNote: "Tailor-made",
    tagline: "For organizations with multiple locations or service lines.",
    features: [
      "Custom-scoped for your organization",
      "Dedicated account manager",
      "Contact us for a tailor-made experience",
    ],
    cta: "Talk to Us",
    isEnterprise: true
  }
];

export default function ProviderSignupPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const paymentCanceled = searchParams.get("payment") === "canceled";
  const inviteToken = searchParams.get("invite");
  const [step, setStep] = useState(1);
  const [promo, setPromo] = useState(null);
  const [payState, setPayState] = useState(sessionId ? "checking" : null);
  const [paidInfo, setPaidInfo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [npiVerifying, setNpiVerifying] = useState(false);
  const [npiVerified, setNpiVerified] = useState(null); // { org_name, address, phone, taxonomy, star_rating }
  const [invite, setInvite] = useState(null); // resolved invite payload
  const [inviteError, setInviteError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/promo`).then((res) => setPromo(res.data)).catch(() => {});
  }, []);

  // If landed with ?invite=<jwt> (admin approved this provider), verify the
  // token, pre-fill the form with their inquiry info, and skip past the tier
  // intro to the details step.
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/provider/invite/${inviteToken}`);
        if (cancelled) return;
        setInvite(data);
        // Map inquiry shape → onboarding form shape.
        // Known Tyvena care-type labels — anything else is dropped silently
        // (the chip UI won't render unknown labels anyway).
        const VALID_CARE_TYPES = new Set([
          "Home Care",
          "Home Health",
          "Palliative Care",
          "Hospice Care",
          "Adult Day Care",
          "Home Physicians",
          "Senior Advisor",
        ]);
        const chips = (data.care_types || "")
          .split(",")
          .map((s) => s.trim())
          .filter((c) => VALID_CARE_TYPES.has(c));
        // Service area on the inquiry is free-text ("Charlotte NC, 28202").
        // If it looks like a comma-separated list of 5-digit zips, use it;
        // otherwise leave blank so the provider fills in properly on this
        // more-structured form.
        const zipCandidate = (data.service_area || "").trim();
        const zipsOnly = /^(\d{5})(\s*,\s*\d{5})*$/.test(zipCandidate) ? zipCandidate : "";
        setFormData((prev) => ({
          ...prev,
          name: prev.name || data.org_name || "",
          email: prev.email || data.email || "",
          phone: prev.phone || data.phone || "",
          zip_codes: prev.zip_codes || zipsOnly,
          care_types: prev.care_types.length ? prev.care_types : chips,
        }));
        // Jump past the tier-explainer intro so they land right in the form
        setStep(2);
        toast.success(`Welcome back, ${(data.contact_name || "").split(" ")[0] || "friend"} — we've pre-filled what you already told us.`);
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.detail || "This invite link couldn't be verified.";
        setInviteError(msg);
        toast.error(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  useEffect(() => {
    if (sessionId) {
      pollPayment(sessionId, 0);
    } else if (paymentCanceled) {
      toast.info("Payment canceled — your registration is saved. You can complete payment below.");
    }
  }, []);

  const pollPayment = async (sid, attempt) => {
    if (attempt >= 15) {
      setPayState("timeout");
      return;
    }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sid}`);
      if (data.payment_status === "paid") {
        setPaidInfo(data);
        setPayState("success");
        sessionStorage.removeItem("myasher_pending_payment");
        return;
      }
      if (data.status === "expired") {
        setPayState("expired");
        return;
      }
      setTimeout(() => pollPayment(sid, attempt + 1), 2000);
    } catch (error) {
      setPayState("error");
    }
  };

  const startPayment = async (providerId, tier) => {
    const { data } = await axios.post(`${API}/payments/checkout`, {
      tier,
      provider_id: providerId,
      origin_url: window.location.origin
    });
    sessionStorage.setItem("myasher_pending_payment", JSON.stringify({ provider_id: providerId, tier }));
    window.location.href = data.url;
  };

  const retryPayment = async () => {
    const pending = sessionStorage.getItem("myasher_pending_payment");
    if (!pending) {
      toast.error("We couldn't find your registration. Please register again or contact us.");
      return;
    }
    try {
      const { provider_id, tier } = JSON.parse(pending);
      await startPayment(provider_id, tier);
    } catch (error) {
      toast.error("Could not restart payment. Please try again.");
    }
  };
  const [selectedTier, setSelectedTier] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    care_types: [],
    zip_codes: "",
    phone: "",
    email: "",
    website: "",
    address: "",
    password: "",
    compensation_model: "",
    // Legal & Credentialing (encrypted at rest, admin-only viewable)
    npi: "",
    state_business_id: "",
    tax_id: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear NPI verification if the user edits the NPI after verifying
    if (name === "npi" && npiVerified) setNpiVerified(null);
  };

  // Look up an NPI in the CMS NPPES registry and pre-fill business fields
  const verifyNpi = async () => {
    const npi = (formData.npi || "").trim();
    if (!/^\d{10}$/.test(npi)) {
      toast.error("NPI must be exactly 10 digits.");
      return;
    }
    setNpiVerifying(true);
    try {
      const { data } = await axios.get(`${API}/providers/npi-lookup/${npi}`);
      setNpiVerified(data);
      // Pre-fill name + phone + address ONLY when the field is empty (never overwrite the user)
      setFormData(prev => ({
        ...prev,
        name: prev.name || data.org_name || prev.name,
        phone: prev.phone || data.phone || prev.phone,
        address: prev.address ||
          [data.address.line1, data.address.city, data.address.state, data.address.zip]
            .filter(Boolean).join(", "),
      }));
      toast.success(`Verified with CMS: ${data.org_name}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't verify that NPI. Please double-check.";
      toast.error(detail);
      setNpiVerified(null);
    } finally {
      setNpiVerifying(false);
    }
  };

  const handleCareTypeToggle = (careType) => {
    setFormData(prev => ({
      ...prev,
      care_types: prev.care_types.includes(careType)
        ? prev.care_types.filter(t => t !== careType)
        : [...prev.care_types, careType]
    }));
  };

  const handleSubmit = async () => {
    if (formData.password && formData.password.length < 8) {
      toast.error("Account password must be at least 8 characters");
      return;
    }
    if (formData.care_types.includes("Senior Advisor") && !formData.compensation_model) {
      toast.error("Senior Advisors must select a compensation model so families can see how you're paid.");
      return;
    }
    // Credentialing: at least one of NPI / State Business ID / EIN required
    const npi = formData.npi.trim();
    const stateBiz = formData.state_business_id.trim();
    const ein = formData.tax_id.trim();
    if (!npi && !stateBiz && !ein) {
      toast.error("Please provide at least one credential — NPI (preferred), State Business ID, or EIN.");
      return;
    }
    if (npi && !/^\d{10}$/.test(npi)) {
      toast.error("NPI must be exactly 10 digits.");
      return;
    }
    if (ein && !/^\d{2}-?\d{7}$/.test(ein)) {
      toast.error("EIN must be 9 digits (format XX-XXXXXXX).");
      return;
    }
    setIsSubmitting(true);
    try {
      const zipArray = formData.zip_codes.split(",").map(z => z.trim()).filter(z => z);
      const { password, ...listing } = formData;

      const { data: reg } = await axios.post(`${API}/providers/register`, {
        ...listing,
        npi: npi || null,
        state_business_id: stateBiz || null,
        tax_id: ein || null,
        zip_codes: zipArray,
        subscription_tier: selectedTier
      });

      // Create the provider account so they can access their dashboard
      if (password) {
        try {
          const { data: account } = await axios.post(`${API}/auth/provider-account`, {
            name: formData.name,
            email: formData.email,
            password
          }, { withCredentials: true });
          setUser(account);
        } catch (accountError) {
          const detail = accountError.response?.data?.detail;
          toast.info(typeof detail === "string" ? detail : "Listing submitted — but the account could not be created.");
        }
      }

      // Redirect to secure Stripe Checkout for the selected tier
      toast.success("Registration saved! Taking you to secure checkout...");
      await startPayment(reg.provider_id, selectedTier);
    } catch (error) {
      console.error("Error registering:", error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Registration failed. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => step === 1 ? navigate("/") : setStep(step - 1)}
              variant="ghost"
              className="text-primary hover:bg-primary/5"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-heading font-bold text-primary">Provider Registration</h1>
          </div>
        </div>
      </header>

      {/* Payment status screen (after returning from Stripe Checkout) */}
      {payState && (
        <main className="max-w-xl mx-auto px-4 py-20">
          <div data-testid="payment-status-card" className="bg-white rounded-2xl border border-[#E2E8F0] shadow-lg p-10 text-center space-y-6">
            {payState === "checking" && (
              <>
                <Loader2 className="w-14 h-14 text-primary mx-auto animate-spin" />
                <h2 className="text-2xl font-heading font-bold text-text-primary">Confirming your payment...</h2>
                <p className="text-text-secondary font-body">This usually takes just a few seconds. Please don&apos;t close this page.</p>
              </>
            )}
            {payState === "success" && (
              <>
                <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" />
                <h2 className="text-2xl font-heading font-bold text-text-primary">Payment Successful! 🎉</h2>
                <p className="text-text-secondary font-body">
                  Your <span className="font-bold capitalize">{paidInfo?.tier}</span> subscription
                  {paidInfo?.amount ? ` ($${Number(paidInfo.amount).toLocaleString()}/year)` : ""} is confirmed.
                  Our team will review and activate your listing within 24 hours.
                </p>
                <Button
                  data-testid="payment-success-dashboard-btn"
                  onClick={() => navigate("/provider-dashboard")}
                  className="min-h-[48px] px-8 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold"
                >
                  Go to Your Provider Dashboard
                </Button>
              </>
            )}
            {(payState === "expired" || payState === "error" || payState === "timeout") && (
              <>
                <XCircle className="w-16 h-16 text-red-500 mx-auto" />
                <h2 className="text-2xl font-heading font-bold text-text-primary">
                  {payState === "expired" ? "Payment Session Expired" : payState === "timeout" ? "Still Processing" : "Something Went Wrong"}
                </h2>
                <p className="text-text-secondary font-body">
                  {payState === "timeout"
                    ? "Your payment may still be processing — check your email for a receipt, or try again."
                    : "Your registration is saved. You can try the payment again below."}
                </p>
                <div className="flex gap-3 justify-center">
                  <Button
                    data-testid="payment-retry-btn"
                    onClick={retryPayment}
                    className="min-h-[48px] px-8 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold"
                  >
                    <CreditCard className="w-4 h-4 mr-2" /> Try Payment Again
                  </Button>
                  <Button onClick={() => navigate("/")} variant="outline" className="min-h-[48px] px-6 rounded-full font-bold">
                    Back to Home
                  </Button>
                </div>
              </>
            )}
          </div>
        </main>
      )}

      {!payState && (
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Canceled payment: offer retry */}
        {paymentCanceled && sessionStorage.getItem("myasher_pending_payment") && (
          <div data-testid="payment-canceled-banner" className="max-w-2xl mx-auto mb-8 rounded-2xl p-5 bg-amber-50 border-2 border-amber-300 text-center shadow-sm">
            <p className="font-heading font-bold text-text-primary">Your registration is saved — payment is still pending.</p>
            <Button
              data-testid="payment-canceled-retry-btn"
              onClick={retryPayment}
              className="mt-3 min-h-[44px] px-6 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold"
            >
              <CreditCard className="w-4 h-4 mr-2" /> Complete Payment
            </Button>
          </div>
        )}
        {/* Step 1: Choose Tier */}
        {step === 1 && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-3xl font-heading font-bold text-text-primary mb-4">
                Choose Your Listing Level
              </h2>
              <p className="text-lg text-text-secondary font-body">
                Start free. Add depth to your profile whenever you&apos;re ready — never a pressure sale, and never a &ldquo;leads package.&rdquo;
              </p>
            </div>

            {/* Fair-placement pledge — visible to every prospective provider */}
            <div
              data-testid="fair-placement-pledge"
              className="max-w-3xl mx-auto rounded-2xl border-2 border-primary/25 bg-primary/5 p-5 md:p-6"
            >
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="font-heading font-bold text-primary text-base mb-1">
                    Families come first — that&apos;s our pledge to them, and to you.
                  </p>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">
                    Tyvena helps families discover the services, care and community resources they need for an aging loved one — right when they need them. Families choose who they want to learn more about and when they&apos;re ready to reach out. Higher tiers unlock <em>your own profile depth</em> — photos, video, virtual consult booking — so you can share your expertise, your services, and the difference you make. Read our{" "}
                    <a href="/tier-policy" className="text-primary underline hover:no-underline">
                      full provider policy
                    </a>.
                  </p>
                </div>
              </div>
            </div>

            {promo?.enabled && (
              <div
                data-testid="signup-promo-banner"
                className="max-w-2xl mx-auto rounded-2xl p-5 bg-gradient-to-r from-primary/15 via-secondary/10 to-primary/15 border-2 border-primary/40 text-center shadow-md"
              >
                <p className="text-xl font-heading font-bold text-text-primary">{promo.title}</p>
                <p className="font-body text-text-secondary mt-1">{promo.description}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {TIERS.map((tier) => (
                <div
                  key={tier.name}
                  data-testid={`tier-card-${tier.name}`}
                  className={`relative bg-white rounded-2xl border-2 p-6 transition-all duration-300 cursor-pointer flex flex-col ${
                    selectedTier === tier.name
                      ? "border-primary shadow-xl scale-[1.02]"
                      : "border-[#E2E8F0] hover:border-primary/50 hover:shadow-lg"
                  }`}
                  onClick={() => !tier.isEnterprise && setSelectedTier(tier.name)}
                >
                  <div className="mb-4">
                    <p className="text-xs font-body font-semibold uppercase tracking-wide text-text-secondary">
                      {tier.tierNumber}
                    </p>
                    <h3 className="text-2xl font-heading font-bold text-text-primary">
                      {tier.displayName}
                    </h3>
                    {tier.tagline && (
                      <p className="text-sm font-body text-text-secondary italic mt-1">
                        {tier.tagline}
                      </p>
                    )}
                  </div>

                  <div className="mb-5">
                    {tier.originalPrice && (
                      <p className="text-sm font-body text-text-secondary line-through">
                        {tier.originalPrice}
                      </p>
                    )}
                    <p className="text-2xl font-heading font-bold text-primary">
                      {tier.price}
                    </p>
                    {tier.priceNote && (
                      <p className="text-xs font-body text-text-secondary mt-0.5">{tier.priceNote}</p>
                    )}
                    {tier.promoLabel && (
                      <div
                        data-testid={`promo-badge-${tier.name}`}
                        className="mt-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary/15 border border-secondary/30 text-secondary text-[11px] font-body font-semibold"
                      >
                        {tier.promoLabel}
                      </div>
                    )}
                  </div>

                  <ul className="space-y-2.5 mb-6 flex-1">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm font-body text-text-secondary">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {tier.isEnterprise ? (
                    <Button
                      data-testid={`tier-enterprise-cta`}
                      onClick={(e) => { e.stopPropagation(); setShowEnterpriseModal(true); }}
                      className="w-full h-11 rounded-full font-bold bg-accent text-white hover:bg-accent/90"
                    >
                      {tier.cta}
                    </Button>
                  ) : (
                    <Button
                      data-testid={`tier-cta-${tier.name}`}
                      className={`w-full h-11 rounded-full font-bold transition-all ${
                        selectedTier === tier.name
                          ? "bg-primary text-white"
                          : "bg-background text-primary border-2 border-primary hover:bg-primary hover:text-white"
                      }`}
                      onClick={(e) => { e.stopPropagation(); setSelectedTier(tier.name); }}
                    >
                      {selectedTier === tier.name ? "Selected" : tier.cta}
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="text-center">
              <Button
                data-testid="continue-to-registration-btn"
                disabled={!selectedTier}
                onClick={() => setStep(2)}
                className="min-h-[48px] px-12 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold text-lg disabled:opacity-50"
              >
                Continue to Registration
              </Button>
            </div>

            {/* Educational library invitation — open to ALL providers, not a paid feature */}
            <div
              data-testid="library-invitation"
              className="max-w-3xl mx-auto rounded-2xl border border-primary/20 bg-white p-5 md:p-6"
            >
              <div className="flex items-start gap-3">
                <HeartHandshake className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="font-heading font-bold text-text-primary text-base mb-1">
                    Share your expertise with families
                  </p>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">
                    Every listed provider — free or paid — is welcome to submit articles for potential
                    inclusion in the Tyvena educational library. Every submission is reviewed by our
                    team before publishing, so quality and neutrality stay in your family&apos;s corner.
                  </p>
                </div>
              </div>
            </div>

            {/* Community Partner inquiry callout */}
            <div
              data-testid="community-partner-cta"
              className="mt-12 rounded-2xl border-2 border-secondary/30 bg-gradient-to-br from-secondary/8 via-white to-primary/5 p-6 md:p-8 max-w-3xl mx-auto"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-secondary/15 flex items-center justify-center">
                  <HeartHandshake className="w-6 h-6 text-secondary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-heading font-bold text-text-primary">
                    Are you a nonprofit or community organization?
                  </h3>
                  <p className="text-text-secondary font-body mt-2">
                    Churches, Meals on Wheels, Area Agencies on Aging, disease-specific nonprofits, caregiver support groups, and other mission-driven partners can be listed as <strong className="text-secondary">Community Partners</strong>. Free of charge — in exchange for helping share Tyvena with the families you serve.
                  </p>
                  <Button
                    data-testid="community-partner-inquiry-btn"
                    onClick={() => setShowPartnerModal(true)}
                    className="mt-4 bg-secondary text-white hover:bg-secondary/90 rounded-full font-semibold"
                  >
                    Submit a Community Partner inquiry
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Community Partner Inquiry Modal */}
        <CommunityPartnerInquiryModal
          open={showPartnerModal}
          onClose={() => setShowPartnerModal(false)}
        />

        {/* Enterprise (Tier 4) Inquiry Modal */}
        <EnterpriseInquiryModal
          open={showEnterpriseModal}
          onClose={() => setShowEnterpriseModal(false)}
        />

        {/* Step 2: Provider Information */}
        {step === 2 && (
          <div className="max-w-3xl mx-auto space-y-8">
            {invite && (
              <div
                data-testid="invite-welcome-banner"
                className="rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-[#EDF7F6] to-white p-5 md:p-6 flex items-start gap-4"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-heading font-bold text-primary text-lg mb-1">
                    Welcome back, {(invite.contact_name || "").split(" ")[0] || "friend"} — you&apos;re in.
                  </p>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">
                    We&apos;ve pre-filled your organization name, contact info, and the types of care you told us about. Pick your tier below, review the pre-filled details, and add anything you&apos;d like families to see. That&apos;s it.
                  </p>
                </div>
              </div>
            )}
            <div className="text-center">
              <h2 className="text-3xl font-heading font-bold text-text-primary mb-2">
                Build Your Listing
              </h2>
              <p className="text-lg text-text-secondary font-body">
                {TIERS.find(t => t.name === selectedTier)?.displayName} Plan
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-8 space-y-6">
              <div>
                <Label htmlFor="name" className="font-body font-semibold text-text-primary">
                  Company/Agency Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-2 h-12 font-body"
                  placeholder="e.g., Premier Home Health Services"
                />
              </div>

              <div>
                <Label htmlFor="description" className="font-body font-semibold text-text-primary">
                  Description *
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  className="mt-2 font-body min-h-[120px]"
                  placeholder="Describe your services, specialties, and what makes your agency unique..."
                />
              </div>

              <div>
                <Label className="font-body font-semibold text-text-primary mb-3 block">
                  Care Types Offered *
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {["Home Care", "Home Health", "Palliative Care", "Hospice Care", "Senior Advisor", "Adult Day Care", "Home Physicians", "Community Partners"].map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleCareTypeToggle(type)}
                      className={`p-4 rounded-lg border-2 font-body font-semibold transition-all ${
                        formData.care_types.includes(type)
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-[#E2E8F0] text-text-secondary hover:border-primary/50"
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Senior Advisor compensation model — required when "Senior Advisor" is selected */}
              {formData.care_types.includes("Senior Advisor") && (
                <div data-testid="advisor-compensation-section">
                  <Label className="font-body font-semibold text-text-primary mb-3 block">
                    How are you compensated? * <span className="font-normal text-text-secondary">(required for Senior Advisor listings — shown as a badge so families know up front)</span>
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      {
                        value: "independent",
                        title: "Private pay (independent)",
                        sub: "Families pay you directly. No referral fees from communities or services."
                      },
                      {
                        value: "community_paid",
                        title: "Community-paid (free to families)",
                        sub: "Communities or service partners pay you for successful referrals. Free to the family."
                      }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`advisor-comp-${opt.value}`}
                        onClick={() => setFormData(prev => ({ ...prev, compensation_model: opt.value }))}
                        className={`p-4 rounded-lg border-2 font-body text-left transition-all ${
                          formData.compensation_model === opt.value
                            ? "border-primary bg-primary/10"
                            : "border-[#E2E8F0] hover:border-primary/50"
                        }`}
                      >
                        <div className={`font-semibold ${formData.compensation_model === opt.value ? "text-primary" : "text-text-primary"}`}>
                          {opt.title}
                        </div>
                        <div className="text-xs text-text-secondary mt-1">{opt.sub}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="zip_codes" className="font-body font-semibold text-text-primary">
                  Zip Codes Served * (comma-separated)
                </Label>
                <Input
                  id="zip_codes"
                  name="zip_codes"
                  value={formData.zip_codes}
                  onChange={handleChange}
                  required
                  className="mt-2 h-12 font-body"
                  placeholder="e.g., 10001, 10002, 10003"
                />
                <p className="text-sm text-text-secondary mt-1 font-body">
                  All the zip codes your office covers — tied to your one office location.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone" className="font-body font-semibold text-text-primary">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-2 h-12 font-body"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="font-body font-semibold text-text-primary">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 h-12 font-body"
                    placeholder="contact@company.com"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="website" className="font-body font-semibold text-text-primary">
                  Website (optional)
                </Label>
                <Input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleChange}
                  className="mt-2 h-12 font-body"
                  placeholder="https://yourcompany.com"
                />
              </div>

              <div>
                <Label htmlFor="address" className="font-body font-semibold text-text-primary">
                  Office Address *
                </Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="mt-2 h-12 font-body"
                  placeholder="123 Main St, City, State ZIP"
                />
              </div>

              {/* ─── Legal & Credentialing (encrypted at rest, admin-only) ─── */}
              <div data-testid="legal-credentialing-section" className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-5 md:p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Lock className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-accent text-lg leading-tight">Legal &amp; Credentialing (Private)</p>
                    <p className="text-xs md:text-sm text-text-secondary font-body leading-relaxed mt-1">
                      Encrypted and stored securely — <strong>only visible to Tyvena administrators for verification</strong>, never shown on your public listing.
                      Please provide <strong>at least one</strong> of the following (NPI preferred).
                    </p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="npi" className="font-body font-semibold text-text-primary">
                    National Provider Identifier (NPI) <span className="text-xs font-normal text-text-secondary">— preferred</span>
                  </Label>
                  <div className="mt-2 flex flex-wrap gap-2 items-stretch">
                    <Input
                      id="npi"
                      name="npi"
                      data-testid="provider-signup-npi"
                      value={formData.npi}
                      onChange={handleChange}
                      inputMode="numeric"
                      maxLength={10}
                      className="h-12 font-body font-mono tracking-wider flex-1 min-w-[220px]"
                      placeholder="10-digit number"
                    />
                    <Button
                      type="button"
                      data-testid="npi-verify-btn"
                      onClick={verifyNpi}
                      disabled={npiVerifying || !/^\d{10}$/.test(formData.npi.trim())}
                      className="h-12 px-5 rounded-lg bg-primary text-white hover:bg-[#238C82] font-body font-semibold disabled:opacity-50"
                    >
                      {npiVerifying ? "Verifying…" : "Verify with CMS"}
                    </Button>
                  </div>
                  <p className="text-xs text-text-secondary mt-1 font-body">
                    Look up yours free at <a href="https://npiregistry.cms.hhs.gov/" target="_blank" rel="noopener noreferrer" className="underline text-primary">npiregistry.cms.hhs.gov</a>. We&apos;ll pre-fill your business details and — if your agency is Medicare-certified — pull your official CMS quality star rating.
                  </p>

                  {npiVerified && (
                    <div
                      data-testid="npi-verify-success"
                      className="mt-3 rounded-xl border-2 border-primary/40 bg-primary/5 p-4"
                    >
                      <div className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="font-heading font-bold text-primary">
                            Verified: {npiVerified.org_name}
                          </p>
                          <p className="text-sm font-body text-text-secondary mt-1">
                            {npiVerified.address.line1 && `${npiVerified.address.line1}, `}
                            {npiVerified.address.city}, {npiVerified.address.state} {npiVerified.address.zip}
                          </p>
                          <p className="text-xs font-body text-text-secondary mt-0.5">
                            {npiVerified.taxonomy.desc} · {npiVerified.phone}
                          </p>
                          {npiVerified.star_rating ? (
                            <div
                              data-testid="npi-verify-star-rating"
                              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/15 border border-secondary/30"
                            >
                              <Star className="w-4 h-4 text-secondary fill-secondary" />
                              <span className="text-sm font-body font-semibold text-text-primary">
                                CMS Star Rating: {npiVerified.star_rating.rating.toFixed(1)}
                              </span>
                              <span className="text-xs font-body text-text-secondary">
                                (auto-populated on your profile)
                              </span>
                            </div>
                          ) : (
                            <p className="text-xs font-body text-text-secondary mt-2">
                              No CMS star rating available for this care type — that&apos;s normal for non-Medicare-certified services.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="state_business_id" className="font-body font-semibold text-text-primary">
                      State Business ID / License
                    </Label>
                    <Input
                      id="state_business_id"
                      name="state_business_id"
                      data-testid="provider-signup-state-business-id"
                      value={formData.state_business_id}
                      onChange={handleChange}
                      className="mt-2 h-12 font-body"
                      placeholder="State registration or license #"
                    />
                    <p className="text-xs text-text-secondary mt-1 font-body">
                      If you don&apos;t have an NPI.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="tax_id" className="font-body font-semibold text-text-primary">
                      Federal EIN (Tax ID)
                    </Label>
                    <Input
                      id="tax_id"
                      name="tax_id"
                      data-testid="provider-signup-tax-id"
                      value={formData.tax_id}
                      onChange={handleChange}
                      inputMode="numeric"
                      className="mt-2 h-12 font-body font-mono tracking-wider"
                      placeholder="XX-XXXXXXX"
                    />
                    <p className="text-xs text-text-secondary mt-1 font-body">
                      If neither of the above applies.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="font-body font-semibold text-text-primary">
                  Create Account Password *
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  data-testid="provider-signup-password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                  className="mt-2 h-12 font-body"
                  placeholder="At least 8 characters"
                />
                <p className="text-sm text-text-secondary mt-1 font-body">
                  Creates your secure Provider Dashboard login (using the email above) — manage your listing and join virtual consults with families.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 h-12 rounded-full font-bold"
              >
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                data-testid="provider-signup-submit-btn"
                className="flex-1 h-12 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold disabled:opacity-60"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><CreditCard className="w-4 h-4 mr-2" /> Continue to Secure Checkout</>
                )}
              </Button>
            </div>
          </div>
        )}
      </main>
      )}
    </div>
  );
}
